import {
    User, Role, Deposit, DepositStatus, PaymentFamily, Order, OrderItem,
    OrderStatus, AdminStats, SecurityStats, Product, Category, SubCategory,
    ProductStatus, Ticket, TicketStatus, Notification, NotificationType, NotificationPriority, UserStatus,
    DeliveryMethod, ProductSaleType, PaymentSetting, MediaItem, coerceAdminMethodCode,
    TicketSource, TicketPriority, DigitalCode, StockStat, DeliveryStatus, InventoryMovement, Preference,
    CreateNotificationInput, NotificationFilters, PaymentMethodCode, PAYMENT_METHOD_FAMILY,
    paymentFamilyToCode
} from '../types';
import { formatCurrency, translateError, getDepositMethodLabel } from '../utils/helpers';
import { supabase, getSupabaseFunctionsUrl } from './supabaseClient';
import { deliveryService } from './deliveryService';
import { notifyUser, notifyAdmins } from './notificationService';
import { uploadFile, getFullImageUrl } from './storage/publicAssets';
import { checkStockForItems, orderMethods } from './orders/orderMutations';
import { catalogMethods } from './catalog/catalogMethods';
import { mapProfileToUser } from './api/mappers';
import { isTurnstileDisabled } from './api/constants';
import { verifyTOTPClient, generateRecoveryCodes, hashRecoveryCode } from './auth/totpHelpers';
import { devError } from '@/utils/devLog';

export { mapProfileToUser };

function normalizePaymentSetting(row: Record<string, unknown>): PaymentSetting {
    // La BDD est censée avoir été migrée, mais par sécurité on cast via une valeur fallback si la donnée est corrompue
    const method_code = (coerceAdminMethodCode(row.method_code) || PaymentMethodCode.PORTEFEUILLE_FLOUCI) as PaymentMethodCode;
    const family = PAYMENT_METHOD_FAMILY[method_code] || PaymentFamily.PORTEFEUILLE;

    return {
        id: row.id as number,
        created_at: row.created_at != null ? String(row.created_at) : undefined,
        provider_name: String(row.provider_name ?? ''),
        identifier: row.identifier != null && row.identifier !== '' ? String(row.identifier) : null,
        iban: row.iban != null ? String(row.iban) : undefined,
        account_holder: row.account_holder != null ? String(row.account_holder) : undefined,
        method_code,
        family,
        for_order: Boolean(row.for_order),
        for_deposit: Boolean(row.for_deposit),
        for_product: Boolean(row.for_product),
        is_enabled: row.is_enabled !== false,
        sort_order: typeof row.sort_order === 'number' ? row.sort_order : 0,
        instructions: row.instructions != null ? String(row.instructions) : undefined,
    };
}

export interface LoginCredentials {
    email: string;
    password?: string;
    turnstileToken?: string;
    twoFACode?: string; // Code TOTP pour la 2FA
    rememberMe?: boolean;
}

export interface RegisterData {
    nom: string;
    prénom: string;
    téléphone: string;
    email: string;
    adresse: string;
    password?: string;
    turnstileToken?: string;
}

export interface NewDepositData {
    user_id: string;
    montant: number;
    méthode: PaymentFamily;
    payment_method_detail?: PaymentMethodCode;
    preuve?: File;
    transaction_id?: string;
}

export interface NewOrderData {
    user_id: string;
    items: OrderItem[];
    montant_total: number;
    mode_paiement?: PaymentFamily;
    payment_setting_id?: number;
    payment_method_detail?: PaymentMethodCode;
    preuve?: File;
    transaction_id?: string;
    delivery_fee?: number;
    stamp_fee?: number;
}

export interface UserProfileData {
    nom: string;
    prénom: string;
    téléphone: string;
    adresse: string;
    avatarFile?: File;
    avatar_url?: string;
}

export interface NewProductData {
    nom: string;
    description?: string;
    prix: number;
    sub_category_id: string;
    statut: ProductStatus;
    est_validé: boolean;
    est_meilleure_vente?: boolean;
    valeur?: string;
    preferences?: Preference[];
    image_url?: string;
    image_file?: File | null;
    logo_url?: string;
    logo_file?: File | null;
    other_images?: string[];
    other_images_files?: File[];
   delivery_method: DeliveryMethod;
    type_vente: ProductSaleType;
    quantite_min: number;
}

export interface UpdateProductData extends NewProductData {
    id: number;
}

export interface NewCategoryData {
    nom: string;
    description: string;
    image_file?: File | null;
    image_url?: string;
}

export interface UpdateCategoryData extends NewCategoryData {
    id: string;
}

export interface NewSubCategoryData {
    nom: string;
    description?: string;
    categorie_id: string;
    image_file?: File | null;
    image_url?: string;
}

export const api = {
    verifyTurnstile: async (token: string): Promise<boolean> => {
        // Si Turnstile est désactivé, retourner true directement
        if (isTurnstileDisabled()) {
            console.log('[DEV] Turnstile désactivé - validation automatique');
            return true;
        }
        
        try {
            const { data, error } = await supabase.rpc('verify_turnstile_rpc', { token });
            if (error || !data || (data as any).success !== true) return false;
            return true;
        } catch (e) {
            return false;
        }
    },

    getCurrentUser: async (): Promise<User | null> => {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) return null;
        const { data: profile, error: userError } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
        if (userError) return null;
        const user = mapProfileToUser(profile);
        // Confirmation native Supabase : si l'utilisateur a cliqué sur le lien, auth a email_confirmed_at
        if (session.user?.email_confirmed_at) {
            user.email_verified = true;
            user.email_confirmed_at = session.user.email_confirmed_at;
        }
        return user;
    },

    login: async (credentials: LoginCredentials): Promise<User | { requires2FA: true }> => {
        // Si un code 2FA est fourni, on est dans la deuxième étape
        // On a déjà validé email/password, donc on peut sauter Turnstile
        const is2FAStep = !!credentials.twoFACode;

        // Vérifier Turnstile uniquement si activé ET si on n'est pas en mode 2FA
        if (!isTurnstileDisabled() && !is2FAStep) {
            if (!credentials.turnstileToken) throw new Error("Veuillez valider le CAPTCHA.");
            const isHuman = await api.verifyTurnstile(credentials.turnstileToken);
            if (!isHuman) throw new Error("Échec de la validation de sécurité.");
        }

        const { data, error } = await supabase.auth.signInWithPassword({
            email: credentials.email,
            password: credentials.password!,
        });

        if (error) {
            if (error.message.includes('email_not_confirmed') || error.message.includes('Email not confirmed')) {
                throw new Error("EMAIL_NOT_CONFIRMED");
            }
            throw new Error(translateError(error.message));
        }

        // Ne pas bloquer la connexion si email non confirmé (Supabase) : le middleware RequireEmailVerified redirigera vers /verify-email selon profile.email_verified

        // Vérifier si l'utilisateur a la 2FA activée (maybeSingle : pas de ligne = 2FA désactivée)
        const { data: securityData } = await supabase
            .from('user_security')
            .select('two_fa_enabled, totp_secret')
            .eq('user_id', data.user.id)
            .maybeSingle();

        const has2FA = securityData?.two_fa_enabled === true;

        // Si 2FA est activée mais aucun code n'est fourni, demander le code
        if (has2FA && !credentials.twoFACode) {
            // Déconnecter la session temporaire créée par signInWithPassword
            await supabase.auth.signOut();
            return { requires2FA: true };
        }

        // Si 2FA est activée et un code est fourni, le vérifier
        if (has2FA && credentials.twoFACode) {
            const secret = securityData.totp_secret;
            if (!secret) {
                await supabase.auth.signOut();
                throw new Error("Erreur de configuration 2FA. Veuillez contacter le support.");
            }

            // Nettoyer le code (supprimer les espaces et caractères non numériques)
            const cleanCode = credentials.twoFACode.replace(/\D/g, '').trim();
            
            if (cleanCode.length !== 6) {
                await supabase.auth.signOut();
                throw new Error("Le code 2FA doit contenir exactement 6 chiffres.");
            }

            // Vérifier le code TOTP
            try {
                const isValid = await verifyTOTPClient(secret, cleanCode);
                if (!isValid) {
                    await supabase.auth.signOut();
                    throw new Error("Code 2FA invalide. Veuillez vérifier que le code est correct et qu'il n'a pas expiré.");
                }
            } catch (error: any) {
                await supabase.auth.signOut();
                if (error.message && error.message.includes('Code 2FA')) {
                    throw error;
                }
                throw new Error("Erreur lors de la vérification du code 2FA. Veuillez réessayer.");
            }

            // Code valide : mettre à jour la date de dernière validation 2FA
            await supabase
                .from('user_security')
                .update({ last_2fa_at: new Date().toISOString() })
                .eq('user_id', data.user.id);
        }

        const { data: user, error: userError } = await supabase.from('profiles').select('*').eq('id', data.user.id).single();
        if (userError || !user) throw new Error("Profil introuvable.");
        return mapProfileToUser(user);
    },

    register: async (data: RegisterData): Promise<void> => {
        // Vérifier Turnstile uniquement si activé
        if (!isTurnstileDisabled()) {
            if (!data.turnstileToken) throw new Error("Veuillez valider le CAPTCHA.");
            const isHuman = await api.verifyTurnstile(data.turnstileToken);
            if (!isHuman) throw new Error("Échec de la validation de sécurité.");
        }

        // Validation des champs requis
        if (!data.prénom || !data.prénom.trim()) {
            throw new Error("Le prénom est requis.");
        }
        if (!data.email || !data.email.trim()) {
            throw new Error("L'email est requis.");
        }
        if (!data.password || data.password.length < 6) {
            throw new Error("Le mot de passe doit contenir au moins 6 caractères.");
        }

        const { data: authData, error: authError } = await supabase.auth.signUp({
            email: data.email.trim(),
            password: data.password!,
            options: { 
                data: { 
                    nom: data.nom?.trim() || '', 
                    prénom: data.prénom.trim(), 
                    téléphone: data.téléphone?.trim() || '', 
                    adresse: data.adresse?.trim() || '', 
                    role: Role.CLIENT 
                } 
            }
        });

        if (authError) throw new Error(translateError(authError));
        if (!authData.user) throw new Error("Erreur lors de la création du compte utilisateur.");

        // Attendre que le trigger s'exécute (plusieurs tentatives avec délais progressifs)
        let profile = null;
        const maxRetries = 8; // Augmenté à 8 tentatives
        const initialDelay = 300; // Délai initial plus court
        const maxDelay = 2000; // Délai maximum

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            // Délai progressif : 300ms, 500ms, 700ms, 1000ms, 1500ms, 2000ms...
            const delay = Math.min(initialDelay + (attempt * 200), maxDelay);
            if (attempt > 0) {
                await new Promise(resolve => setTimeout(resolve, delay));
            }

            const { data: fetchedProfile, error: fetchedError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', authData.user.id)
                .single();

            if (fetchedProfile) {
                profile = fetchedProfile;
                break;
            }

            // Si ce n'est pas une erreur "pas de résultat", logger pour debug
            if (fetchedError && fetchedError.code !== 'PGRST116') {
                console.warn(`Tentative ${attempt + 1} de récupération de profil:`, fetchedError.message);
            }
        }

        // Si le profil n'existe toujours pas après les tentatives, essayer de le créer manuellement
        if (!profile) {
            console.warn('Le profil n\'a pas été créé par le trigger après plusieurs tentatives, tentative de création manuelle...');
            
            const { error: insertError } = await supabase
                .from('profiles')
                .insert({
                    id: authData.user.id,
                    email: data.email.trim(),
                    nom: data.nom?.trim() || null,
                    prénom: data.prénom.trim(),
                    téléphone: data.téléphone?.trim() || null,
                    adresse: data.adresse?.trim() || null,
                    role: Role.CLIENT,
                    solde: 0,
                    statut: UserStatus.ACTIVE
                });

            // Si l'erreur est une clé dupliquée, c'est que le trigger a créé le profil entre-temps
            if (insertError) {
                if (insertError.code === '23505' || insertError.message.includes('duplicate key') || insertError.message.includes('profiles_pkey')) {
                    console.log('Le profil existe déjà (créé par le trigger), nouvelle tentative de récupération...');
                    
                    // Attendre un peu plus et réessayer plusieurs fois
                    for (let retry = 0; retry < 5; retry++) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                        const { data: existingProfile } = await supabase
                            .from('profiles')
                            .select('*')
                            .eq('id', authData.user.id)
                            .single();
                        
                        if (existingProfile) {
                            profile = existingProfile;
                            break;
                        }
                    }
                    
                    // Si on n'a toujours pas le profil mais qu'on sait qu'il existe, 
                    // considérer l'inscription comme réussie (le profil sera accessible après confirmation email)
                    if (!profile) {
                        console.warn('Le profil existe mais ne peut pas être récupéré immédiatement. L\'inscription est considérée comme réussie.');
                        // Ne pas jeter d'erreur - l'inscription est réussie, le profil sera accessible après confirmation
                        profile = null; // On accepte que le profil ne soit pas récupéré maintenant
                    }
                } else {
                    devError('Erreur lors de la création manuelle du profil:', insertError);
                    throw new Error(`Erreur lors de la création du profil: ${insertError.message}`);
                }
            } else {
                // Le profil a été créé avec succès, le récupérer
                await new Promise(resolve => setTimeout(resolve, 500)); // Attendre un peu
                const { data: newProfile } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', authData.user.id)
                    .single();

                if (newProfile) {
                    profile = newProfile;
                } else {
                    // Même si on ne peut pas récupérer le profil maintenant, l'inscription est réussie
                    console.warn('Le profil a été créé mais ne peut pas être récupéré immédiatement.');
                }
            }
        }

        // Notifier les admins si on a réussi à récupérer le profil
        // Sinon, la notification sera envoyée lors de la première connexion
        if (profile) {
            try {
                const newUser = mapProfileToUser(profile);
            await notifyAdmins(
                `Nouveau client inscrit : ${data.prénom} ${data.nom || ''}`,
                NotificationType.NOUVEAU_CLIENT,
                `/admin/users/${newUser.id}`,
                undefined,
                { user_id: newUser.id, prénom: data.prénom, nom: data.nom || '' }
            );
            } catch (notifyError) {
                console.warn('Erreur lors de la notification aux admins:', notifyError);
                // Ne pas faire échouer l'inscription pour une erreur de notification
            }
        }

        // L'inscription est réussie même si on n'a pas pu récupérer le profil
        // L'utilisateur devra confirmer son email avant de pouvoir se connecter
        // Le profil sera accessible après la confirmation
    },

    logout: async () => { await supabase.auth.signOut(); },

    loginWithGoogle: async (): Promise<void> => {
        // Rediriger vers l'URL d'authentification Google
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: `${window.location.origin}/oauth/callback`,
                queryParams: {
                    access_type: 'offline',
                    prompt: 'consent',
                },
            },
        });

        if (error) {
            // Message d'erreur plus explicite pour le provider non activé
            if (error.message.includes('not enabled') || error.message.includes('validation_failed')) {
                throw new Error(
                    'Le provider Google n\'est pas activé dans Supabase. ' +
                    'Veuillez activer Google OAuth dans le dashboard Supabase : ' +
                    'Authentication → Providers → Google. ' +
                    'Consultez GOOGLE_OAUTH_SETUP.md pour les instructions détaillées.'
                );
            }
            throw new Error(`Erreur lors de l'authentification Google: ${error.message}`);
        }

        // La redirection se fait automatiquement, pas besoin de retourner quelque chose
    },

    /** Inscription classique : init sans création BDD. Retourne token pour /verify-phone. */
    registerInit: async (data: { email: string; password: string; nom?: string; prénom: string; adresse?: string }): Promise<{ token: string }> => {
        const { data: result, error } = await supabase.functions.invoke<{ success: boolean; token?: string; error?: string }>('register-init', {
            body: {
                email: data.email.trim(),
                password: data.password,
                nom: data.nom?.trim() || null,
                prénom: data.prénom.trim(),
                adresse: data.adresse?.trim() || null,
            },
        });
        if (error) throw new Error(error.message || "Erreur lors de l'initiation de l'inscription.");
        if (!result?.success || !result.token) {
            if (result?.error === 'email_already_used') throw new Error('Cet email est déjà utilisé.');
            throw new Error(result?.error || "Erreur lors de l'initiation de l'inscription.");
        }
        return { token: result.token };
    },

    handleOAuthCallback: async (): Promise<User | { pending: true; token: string }> => {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
            throw new Error('Session introuvable. Veuillez réessayer.');
        }

        const baseUrl = getSupabaseFunctionsUrl();
        const res = await fetch(`${baseUrl}/google-pending`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
            throw new Error(data?.error === 'unauthorized' ? 'Session introuvable.' : 'Erreur serveur.');
        }

        if (data.pending === true && data.token) {
            return { pending: true, token: data.token };
        }

        if (data.user) {
            const user = mapProfileToUser(data.user);
            return user;
        }

        throw new Error('Réponse serveur invalide.');
    },

    getAllUsers: async (): Promise<User[]> => {
        // Utiliser la fonction RPC pour récupérer les utilisateurs avec tous les statuts
        const { data, error } = await supabase.rpc('get_users_with_email_status');
        if (error) {
            // Fallback : lecture directe dans profiles
            console.warn('RPC get_users_with_email_status failed, using fallback:', error.message);
            const { data: fallbackData, error: fallbackError } = await supabase
                .from('profiles')
                .select('*')
                .order('date_creation', { ascending: false });
            if (fallbackError) throw new Error(translateError(fallbackError));
            return fallbackData.map(mapProfileToUser);
        }
        return data.map(mapProfileToUser);
    },

    getUserById: async (userId: string): Promise<any> => {
        // Spécifier explicitement toutes les colonnes pour s'assurer que 'adresse' est bien récupérée.
        // Cast du résultat : le select avec colonnes accentuées peut être inféré en ParserError par le client Supabase.
        const { data: rawUser, error } = await supabase
            .from('profiles')
            .select('id, nom, prénom, email, téléphone, adresse, role, solde, date_creation, statut, avatar_url, deleted_at, email_confirmed_at, email_verified, email_verified_at, phone_verified_at, is_play_store_test_account')
            .eq('id', userId)
            .single();
        if (error) throw new Error(translateError(error));

        type ProfileRow = { id: string; nom: string; prénom: string; email: string; téléphone: string; adresse: string | null; role: string; solde: number; date_creation: string; statut: string; avatar_url: string | null; deleted_at: string | null; email_confirmed_at: string | null; email_verified?: boolean; email_verified_at?: string | null; phone_verified_at?: string | null; is_play_store_test_account?: boolean };
        const user = rawUser as unknown as ProfileRow | null;

        // Debug: vérifier que adresse est bien récupérée
        if (user && !user.adresse && user.email) {
            console.warn('[getUserById] Adresse manquante pour utilisateur:', userId, 'Email:', user.email);
        }

        const { data: orders } = await supabase.from('orders').select('*').eq('user_id', userId).order('date', { ascending: false });
        const { data: deposits } = await supabase.from('deposits').select('*').eq('user_id', userId).order('date', { ascending: false });
        const { data: tickets } = await supabase.from('tickets').select('*').eq('user_id', userId).order('date_maj', { ascending: false });

        const mappedUser = mapProfileToUser(user);
        // S'assurer que adresse est bien préservée dans le mapping
        if (user && user.adresse !== undefined && mappedUser.adresse !== (user.adresse ?? '')) {
            console.warn('[getUserById] Adresse perdue lors du mapping:', { original: user.adresse, mapped: mappedUser.adresse });
            mappedUser.adresse = user.adresse ?? '';
        }

        return { user: mappedUser, orders: orders || [], deposits: deposits || [], tickets: tickets || [] };
    },

    updateUserStatus: async (userId: string, status: UserStatus) => {
        const { error } = await supabase.from('profiles').update({ statut: status }).eq('id', userId);
        if (error) throw new Error(translateError(error));
    },

    updateUserBalance: async (userId: string, newBalance: number) => {
        const { error } = await supabase.from('profiles').update({ solde: newBalance }).eq('id', userId);
        if (error) throw new Error(translateError(error));
    },

    createUserByAdmin: async (data: any) => {
        const { error } = await supabase.rpc('create_user_admin', {
            email: data.email, password: data.password,
            user_data: { nom: data.nom, prénom: data.prénom, téléphone: data.téléphone, adresse: data.adresse, role: Role.CLIENT }
        });
        if (error) throw new Error("Échec de la création administrative.");
    },

    updateUserByAdmin: async (userId: string, data: any) => {
        // Exclure les colonnes calculées et les colonnes qui n'existent pas dans la table profiles
        const payload: any = {};
        
        // Colonnes réelles de la table profiles (selon le schéma)
        const allowedFields = ['nom', 'prénom', 'téléphone', 'email', 'adresse', 'role', 'solde', 'statut', 'avatar_url'];
        
        allowedFields.forEach(field => {
            if (data[field] !== undefined) {
                payload[field] = data[field];
            }
        });
        
        // Gérer avatarUrl (alias de avatar_url)
        if (data.avatarUrl !== undefined) {
            payload.avatar_url = data.avatarUrl;
        }
        
        // Exclure explicitement les colonnes calculées et non modifiables
        const excludedFields = ['id', 'date_creation', 'email_verified', 'email_confirmed_at', 'totalOrders', 'totalDeposits', 'deleted_at'];
        excludedFields.forEach(field => {
            delete payload[field];
        });
        
        const { error } = await supabase.from('profiles').update(payload).eq('id', userId);
        if (error) throw new Error(translateError(error));
    },

    updateUserProfile: async (data: UserProfileData & { id: string }): Promise<User> => {
        const { id, avatarFile, avatar_url, ...rest } = data;
        const updatePayload: any = { ...rest };

        if (avatarFile) {
            updatePayload.avatar_url = await uploadFile('public_pic', avatarFile);
        } else if (avatar_url) {
            updatePayload.avatar_url = avatar_url;
        }

        const { error } = await supabase.from('profiles').update(updatePayload).eq('id', id);
        if (error) throw new Error(translateError(error));

        const { data: user, error: userError } = await supabase.from('profiles').select('*').eq('id', id).single();
        if (userError || !user) throw new Error("Profil introuvable.");
        return mapProfileToUser(user);
    },

    checkUserDependencies: async (userId: string) => {
        const { count: orders } = await supabase.from('orders').select('*', { count: 'exact', head: true }).eq('user_id', userId);
        const { count: deposits } = await supabase.from('deposits').select('*', { count: 'exact', head: true }).eq('user_id', userId);
        const { count: tickets } = await supabase.from('tickets').select('*', { count: 'exact', head: true }).eq('user_id', userId);
        const { count: reviews } = await supabase.from('reviews').select('*', { count: 'exact', head: true }).eq('user_id', userId);
        return { orders: orders || 0, deposits: deposits || 0, tickets: tickets || 0, reviews: reviews || 0, total: (orders || 0) + (deposits || 0) + (tickets || 0) + (reviews || 0) };
    },

    forceDeleteUser: async (userId: string) => {
        // Récupérer les IDs des commandes de l'utilisateur avant de les supprimer
        const { data: userOrders } = await supabase.from('orders').select('id').eq('user_id', userId);
        const orderIds = userOrders?.map(o => o.id) || [];

        await supabase.from('digital_codes').update({ buyer_id: null }).eq('buyer_id', userId);
        
        // Supprimer les delivery_logs via les order_ids (car delivery_logs n'a pas de user_id direct)
        if (orderIds.length > 0) {
            await supabase.from('delivery_logs').delete().in('order_id', orderIds);
        }
        
        await supabase.from('ticket_messages').delete().eq('user_id', userId);
        await supabase.from('tickets').delete().eq('user_id', userId);
        await supabase.from('reviews').delete().eq('user_id', userId);
        await supabase.from('favorites').delete().eq('user_id', userId);
        await supabase.from('notifications').delete().eq('user_id', userId);
        await supabase.from('orders').delete().eq('user_id', userId);
        await supabase.from('deposits').delete().eq('user_id', userId);
        await supabase.from('inventory_movements').delete().eq('user_id', userId);

        const { error: profileError } = await supabase.from('profiles').delete().eq('id', userId);
        if (profileError) throw new Error(translateError(profileError));

        const { error: authError } = await supabase.rpc('delete_auth_user', { user_id: userId });
        if (authError) devError("Auth deletion failed:", authError);
    },

    bulkDeleteUsers: async (userIds: string[]) => {
        for (const userId of userIds) {
            await api.forceDeleteUser(userId);
        }
    },

    getAllDeposits: async (): Promise<Deposit[]> => {
        const { data, error } = await supabase.from('deposits').select('*, user:profiles(nom, prénom, email)').order('date', { ascending: false });
        if (error) throw new Error(translateError(error));
        return (data as any) as Deposit[];
    },

    getDeposits: async (userId: string): Promise<Deposit[]> => {
        const { data, error } = await supabase.from('deposits').select('*').eq('user_id', userId).order('date', { ascending: false });
        if (error) throw new Error(translateError(error));
        return data;
    },

submitDeposit: async (data: NewDepositData): Promise<void> => {
        let preuveUrl = '';
        // La policy RLS du bucket 'proofs' exige un chemin {user_uuid}/{nom} (auth.uid() = storage.foldername(name)[1]).
        if (data.preuve) preuveUrl = await uploadFile('proofs', data.preuve, `${data.user_id}/${Date.now()}_${data.preuve.name.replace(/\s+/g, '_')}`);
        else if (data.transaction_id) preuveUrl = data.transaction_id;

        const paymentMethodCode = data.payment_method_detail || paymentFamilyToCode(data.méthode);
        const { error } = await supabase.from('deposits').insert({
            user_id: data.user_id,
            montant: data.montant,
            payment_method_detail: paymentMethodCode,
            preuve: preuveUrl,
            statut: DepositStatus.PENDING,
            date: new Date().toISOString()
        });
        if (error) throw new Error(translateError(error));

        await notifyAdmins(
            `Nouveau dépôt de ${formatCurrency(data.montant)} reçu via ${getDepositMethodLabel(paymentMethodCode)}`,
            NotificationType.COMMANDE_STATUT, // Fallback as nouveau_depot is not in schema
            `/admin/deposits`,
            undefined,
            { amount: data.montant, method: paymentMethodCode }
        );
    },

    updateDepositStatus: async (id: number, statut: DepositStatus, userId: string, amount: number): Promise<void> => {
        const { error } = await supabase.from('deposits').update({ statut }).eq('id', id);
        if (error) throw new Error(translateError(error));

        if (statut === DepositStatus.VALIDATED) {
            const { data: user } = await supabase.from('profiles').select('solde').eq('id', userId).single();
            if (user) await supabase.from('profiles').update({ solde: user.solde + amount }).eq('id', userId);

            await notifyUser(userId, `Votre dépôt de ${formatCurrency(amount)} a été validé.`, NotificationType.DEPOT_VALIDE, '/dashboard/deposits', undefined, { amount });
        } else if (statut === DepositStatus.REFUSED) {
            await notifyUser(userId, `Votre dépôt de ${formatCurrency(amount)} a été refusé.`, NotificationType.DEPOT_REFUSE, '/dashboard/deposits', undefined, { amount });
        }
    },

    checkStockForItems,
    ...orderMethods,


    ...catalogMethods,


    getMediaLibrary: async (): Promise<MediaItem[]> => {
        const { data } = await supabase.from('media_library').select('*').order('created_at', { ascending: false });
        const items = (data || []) as MediaItem[];
        // Le bucket 'proofs' est privé : générer des URLs signées pour l'affichage.
        await Promise.all(items.map(async item => {
            if (item.bucket_name === 'proofs') {
                const { data: signed } = await supabase.storage.from('proofs').createSignedUrl(item.file_path, 3600);
                if (signed?.signedUrl) item.public_url = signed.signedUrl;
            }
        }));
        return items;
    },

    uploadMedia: async (file: File) => {
        const filePath = `media/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
        const url = await uploadFile('public_pic', file, filePath);
        await supabase.from('media_library').insert({ file_name: file.name, file_path: filePath, bucket_name: 'public_pic', public_url: url, size: file.size, mime_type: file.type });
        return url;
    },

    // Scan récursif de tous les buckets de stockage (les fichiers hors 'public_pic/media'
    // — avatars, proofs, documents, racine de public_pic — ne sont pas dans la table media_library sans ça).
    syncMediaLibrary: async (): Promise<any> => {
        const buckets = ['public_pic', 'avatars', 'proofs', 'documents'];
        const seen = new Set<string>();
        let added = 0;

        const scanFolder = async (bucket: string, prefix: string) => {
            let offset = 0;
            const limit = 1000;
            while (true) {
                const { data: entries, error } = await supabase.storage.from(bucket).list(prefix, { limit, offset, sortBy: { column: 'name', order: 'asc' } });
                if (error) { devError(`Sync: impossible de lister ${bucket}/${prefix}`, error); break; }
                if (!entries || entries.length === 0) break;
                for (const entry of entries) {
                    if (entry.id === null) {
                        // Dossier -> récursion
                        await scanFolder(bucket, `${prefix}/${entry.name}`);
                    } else {
                        const path = `${prefix}/${entry.name}`;
                        seen.add(`${bucket}/${path}`);
                        const { data: existing } = await supabase.from('media_library').select('id').eq('bucket_name', bucket).eq('file_path', path).maybeSingle();
                        if (!existing) {
                            const publicUrl = supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
                            const { error: insertError } = await supabase.from('media_library').insert({
                                file_name: entry.name,
                                file_path: path,
                                bucket_name: bucket,
                                public_url: publicUrl,
                                size: (entry as any).metadata?.size || 0,
                                mime_type: (entry as any).metadata?.mimetype || 'application/octet-stream'
                            });
                            if (!insertError) added++; else devError(`Sync: insertion échouée pour ${bucket}/${path}`, insertError);
                        }
                    }
                }
                if (entries.length < limit) break;
                offset += limit;
            }
        };

        for (const bucket of buckets) {
            await scanFolder(bucket, '');
        }

        // Nettoyage : supprimer les entrées de ces buckets qui pointent vers des fichiers disparus
        // (inclut les anciennes lignes de public_pic avec un file_path erroné).
        const { data: allEntries } = await supabase.from('media_library').select('id, bucket_name, file_path');
        const stale = (allEntries || []).filter(m => buckets.includes(m.bucket_name) && !seen.has(`${m.bucket_name}/${m.file_path}`));
        for (const item of stale) {
            await supabase.from('media_library').delete().eq('id', item.id);
        }

        return { totalScanned: seen.size, added, removed: stale.length };
    },

    checkMediaUsage: async (url: string) => {
        const { count: inProducts } = await supabase.from('products').select('*', { count: 'exact', head: true }).or(`image_url.eq.${url},logo_url.eq.${url}`);
        const { count: inCategories } = await supabase.from('categories').select('*', { count: 'exact', head: true }).eq('image_url', url);
        return { inProducts: inProducts || 0, inCategories: inCategories || 0 };
    },

    updateMediaMetadata: async (id: number, name: string) => { await supabase.from('media_library').update({ file_name: name }).eq('id', id); },
    deleteMedia: async (media: MediaItem) => { await supabase.storage.from(media.bucket_name).remove([media.file_path]); await supabase.from('media_library').delete().eq('id', media.id); },

    getAdminStats: async (): Promise<AdminStats> => {
        const { count: totalUsers } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
        const { count: totalOrders } = await supabase.from('orders').select('*', { count: 'exact', head: true });
        const { count: pendingDeposits } = await supabase.from('deposits').select('*', { count: 'exact', head: true }).eq('statut', 'en_attente');
        const { data: orders } = await supabase.from('orders').select('montant_total, statut').limit(1000);
        let totalRevenue = 0;
        const statusCounts: Record<string, number> = {};
        orders?.forEach(o => { if (o.statut === OrderStatus.COMPLETED) totalRevenue += o.montant_total; statusCounts[o.statut] = (statusCounts[o.statut] || 0) + 1; });
        return { totalUsers: totalUsers || 0, totalOrders: totalOrders || 0, pendingDeposits: pendingDeposits || 0, totalRevenue, revenueByOperator: [{ name: 'Global', value: totalRevenue }], ordersByStatus: Object.entries(statusCounts).map(([name, value]) => ({ name, value })) };
    },

    getAdminSecurityStats: async (): Promise<SecurityStats> => {
        const now = new Date();
        const isoNow = now.toISOString();
        const iso24hAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
        const iso30dAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();

        // IP & OTP
        const [{ count: blockedActive }, { count: blockedLast24h }] = await Promise.all([
            supabase.from('blocked_ips').select('*', { count: 'exact', head: true }).gt('blocked_until', isoNow),
            supabase.from('blocked_ips').select('*', { count: 'exact', head: true }).gt('created_at', iso24hAgo),
        ]);

        const { count: ipActivity24h } = await supabase
            .from('ip_activity')
            .select('ip', { count: 'exact', head: true })
            .gt('last_attempt', iso24hAgo);

        const { count: otpSent24h } = await supabase
            .from('otp_verifications')
            .select('*', { count: 'exact', head: true });

        const { count: otpValidated24h } = await supabase
            .from('otp_verifications')
            .select('*', { count: 'exact', head: true })
            .eq('is_used', true);

        const otpSuccessRate24h =
            (otpSent24h && otpSent24h > 0 && otpValidated24h)
                ? (otpValidated24h / otpSent24h) * 100
                : 0;

        // Tickets & reviews
        const { count: openTickets } = await supabase
            .from('tickets')
            .select('*', { count: 'exact', head: true })
            .eq('statut', TicketStatus.OPEN);

        let avgFirstResponseHours: number | null = null;
        const { data: ticketsData } = await supabase
            .from('tickets')
            .select('id, date_creation')
            .limit(200);
        if (ticketsData && ticketsData.length > 0) {
            const ticketIds = ticketsData.map((t: any) => t.id);
            const { data: messagesData } = await supabase
                .from('ticket_messages')
                .select('ticket_id, date')
                .in('ticket_id', ticketIds)
                .order('date', { ascending: true });
            if (messagesData && messagesData.length > 0) {
                const firstByTicket: Record<number, Date> = {};
                (messagesData as any[]).forEach(msg => {
                    if (!firstByTicket[msg.ticket_id]) {
                        firstByTicket[msg.ticket_id] = new Date(msg.date);
                    }
                });
                const diffs: number[] = [];
                (ticketsData as any[]).forEach(t => {
                    const first = firstByTicket[t.id];
                    if (first) {
                        const created = new Date(t.date_creation);
                        const diffHours = (first.getTime() - created.getTime()) / (1000 * 60 * 60);
                        if (diffHours >= 0) diffs.push(diffHours);
                    }
                });
                if (diffs.length > 0) {
                    avgFirstResponseHours = diffs.reduce((a, b) => a + b, 0) / diffs.length;
                }
            }
        }

        const { data: ratingAgg } = await supabase
            .from('reviews')
            .select('avg(rating)')
            .gt('created_at', iso30dAgo)
            .maybeSingle();
        const avgRating = ratingAgg && (ratingAgg as any).avg ? Number((ratingAgg as any).avg) : null;

        const { count: reviewsCount30d } = await supabase
            .from('reviews')
            .select('*', { count: 'exact', head: true })
            .gt('created_at', iso30dAgo);

        // Comptes & 2FA
        const { count: expiredAccounts24h } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .gt('deactivated_at', iso24hAgo);

        const { count: expiredAccountsTotal } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .not('deactivated_at', 'is', null);

        const { count: twoFaEnabledUsers } = await supabase
            .from('user_security')
            .select('*', { count: 'exact', head: true })
            .eq('two_fa_enabled', true);

        return {
            blockedIpsActive: blockedActive || 0,
            blockedIpsLast24h: blockedLast24h || 0,
            ipWithActivity24h: ipActivity24h || 0,
            otpSentLast24h: otpSent24h || 0,
            otpValidatedLast24h: otpValidated24h || 0,
            otpSuccessRate24h,
            openTickets: openTickets || 0,
            avgFirstResponseHours,
            avgRating,
            reviewsCount30d: reviewsCount30d || 0,
            expiredAccounts24h: expiredAccounts24h || 0,
            expiredAccountsTotal: expiredAccountsTotal || 0,
            twoFaEnabledUsers: twoFaEnabledUsers || 0,
        };
    },

    getPaymentSettings: async (): Promise<PaymentSetting[]> => {
        const { data, error } = await supabase
            .from('admin_payment_settings')
            .select('*')
            .order('sort_order', { ascending: true })
            .order('id', { ascending: true });
        if (error) {
            throw new Error(translateError(error));
        }
        return (data || []).map((row) => normalizePaymentSetting(row as Record<string, unknown>));
    },

    createPaymentSetting: async (data: Record<string, unknown>) => {
        const { error } = await supabase.from('admin_payment_settings').insert(data);
        if (error) throw new Error(translateError(error));
    },
    updatePaymentSetting: async (id: number, data: Record<string, unknown>) => {
        const { error } = await supabase.from('admin_payment_settings').update(data).eq('id', id);
        if (error) throw new Error(translateError(error));
    },
    deletePaymentSetting: async (id: number) => {
        const { error } = await supabase.from('admin_payment_settings').delete().eq('id', id);
        if (error) throw new Error(translateError(error));
    },

    getAllTickets: async (): Promise<Ticket[]> => {
        // Récupérer tous les tickets avec les informations utilisateur
        const { data: ticketsData, error: ticketsError } = await supabase
            .from('tickets')
            .select('*, user:profiles(id, nom, prénom, email, role)')
            .order('date_maj', { ascending: false });
        
        if (ticketsError) {
            throw new Error(translateError(ticketsError));
        }
        
        if (!ticketsData || ticketsData.length === 0) {
            return [];
        }
        
        // Récupérer tous les messages pour ces tickets
        const ticketIds = ticketsData.map((t: any) => t.id);
        const { data: messagesData, error: messagesError } = await supabase
            .from('ticket_messages')
            .select('*, user:profiles(nom, prénom, role)')
            .in('ticket_id', ticketIds)
            .order('date', { ascending: true });
        
        if (messagesError) {
            devError("Error fetching messages:", messagesError);
            // Continuer même si les messages ne peuvent pas être chargés
        }
        
        // Grouper les messages par ticket_id
        const messagesByTicketId = new Map<number, any[]>();
        if (messagesData) {
            messagesData.forEach((msg: any) => {
                const ticketId = msg.ticket_id;
                if (!messagesByTicketId.has(ticketId)) {
                    messagesByTicketId.set(ticketId, []);
                }
                messagesByTicketId.get(ticketId)!.push(msg);
            });
        }
        
        // Combiner les tickets avec leurs messages
        const ticketsWithMessages = (ticketsData as any[]).map(ticket => ({
            ...ticket,
            messages: messagesByTicketId.get(ticket.id) || []
        }));
        
        return ticketsWithMessages as Ticket[];
    },

    getTicketsForUser: async (userId: string): Promise<Ticket[]> => {
        const { data, error } = await supabase.from('tickets').select('*').eq('user_id', userId).order('date_maj', { ascending: false });
        if (error) throw new Error(translateError(error));
        return data;
    },

    getTicketById: async (id: number): Promise<Ticket> => {
        // Récupérer le ticket
        const { data: ticket, error: ticketError } = await supabase
            .from('tickets')
            .select('*, user:profiles(nom, prénom, email, role)')
            .eq('id', id)
            .single();
        
        if (ticketError || !ticket) {
            throw new Error(translateError(ticketError || "Ticket introuvable"));
        }
        
        // Récupérer les messages
        const { data: messages, error: messagesError } = await supabase
            .from('ticket_messages')
            .select('*')
            .eq('ticket_id', id)
            .order('date', { ascending: true });
        
        if (messagesError) {
            devError("Error fetching messages for ticket:", id, messagesError);
            return { ...(ticket as any), messages: [] };
        }
        
        if (!messages || messages.length === 0) {
            return { ...(ticket as any), messages: [] };
        }
        
        // Récupérer les profils des utilisateurs qui ont envoyé des messages
        const userIds = [...new Set(messages.map((msg: any) => msg.user_id).filter((id: any) => id))];
        
        const userProfilesMap = new Map<string, any>();
        if (userIds.length > 0) {
            const { data: profiles, error: profilesError } = await supabase
                .from('profiles')
                .select('id, nom, prénom, role')
                .in('id', userIds);
            
            if (!profilesError && profiles) {
                profiles.forEach((profile: any) => {
                    userProfilesMap.set(profile.id, {
                        nom: profile.nom,
                        prénom: profile.prénom,
                        role: profile.role
                    });
                });
            }
        }
        
        // Combiner les messages avec les profils
        const formattedMessages = messages.map((msg: any) => ({
            ...msg,
            user: msg.user_id ? userProfilesMap.get(msg.user_id) || null : null
        }));
        return { 
            ...(ticket as any), 
            messages: formattedMessages 
        };
    },

    createTicket: async (data: any): Promise<{ id: number }> => {
        let fileUrl = undefined;
        if (data.fichier_joint) fileUrl = await uploadFile('public_pic', data.fichier_joint);

        let emailContact = data.email_contact;
        let nomContact = data.nom_contact;
        if (!emailContact || !nomContact) {
            const { data: profile } = await supabase
                .from('profiles')
                .select('email, prénom, nom')
                .eq('id', data.user_id)
                .single();
            if (profile) {
                const p = profile as any;
                if (!emailContact) emailContact = (p.email as string | null) ?? null;
                if (!nomContact) {
                    const firstName = (p['prénom'] as string | undefined) || '';
                    const lastName = (p.nom as string | undefined) || '';
                    const fullName = [firstName, lastName].filter(Boolean).join(' ');
                    nomContact = fullName || null;
                }
            }
        }

        const { data: ticket, error } = await supabase.from('tickets').insert({
            user_id: data.user_id,
            sujet: data.sujet,
            statut: TicketStatus.OPEN,
            type: data.type,
            source: data.source || TicketSource.DASHBOARD,
            priority: data.priority || TicketPriority.NORMAL,
            email_contact: emailContact,
            nom_contact: nomContact,
            date_maj: new Date().toISOString(),
        }).select().single();
        if (error) throw new Error(translateError(error));
        await supabase.from('ticket_messages').insert({ ticket_id: ticket.id, user_id: data.user_id, message: data.message, fichier_joint: fileUrl });

        await notifyAdmins(
            `Nouveau ticket de support : ${data.sujet}`,
            NotificationType.NOUVEAU_TICKET,
            `/admin/support/${ticket.id}`,
            undefined,
            { ticket_id: ticket.id, sujet: data.sujet }
        );
        return { id: ticket.id };
    },

    addMessageToTicket: async (data: any) => {
        let fileUrl = undefined;
        if (data.fichier_joint) fileUrl = await uploadFile('public_pic', data.fichier_joint);

        await supabase.from('ticket_messages').insert({ ticket_id: data.ticket_id, user_id: data.user_id, message: data.message, fichier_joint: fileUrl });

        // Determine if we should update status or notify
        // If user is adding message -> status usually becomes OPEN or IN_PROGRESS (already handled somewhat)
        // If admin is adding message -> notify user

        // Get ticket details to know the owner
        const { data: ticket } = await supabase.from('tickets').select('user_id').eq('id', data.ticket_id).single();

        if (ticket) {
            if (ticket.user_id === data.user_id) {
                // User replied
                await supabase.from('tickets').update({ date_maj: new Date().toISOString(), statut: TicketStatus.IN_PROGRESS }).eq('id', data.ticket_id);
                // Notify admins? (Optional, maybe too spammy if every message triggers it, but good for support)
                // await notifyAdmins(`Nouvelle réponse sur le ticket #${data.ticket_id}`, NotificationType.TICKET_REPONSE, `/admin/support/${data.ticket_id}`);
                // Admin replied (or someone else)
                await supabase.from('tickets').update({ date_maj: new Date().toISOString(), statut: TicketStatus.IN_PROGRESS }).eq('id', data.ticket_id);
                await notifyUser(ticket.user_id, `Nouvelle réponse sur votre ticket #${data.ticket_id}`, NotificationType.TICKET_REPONSE, '/dashboard/support', undefined, { ticket_id: data.ticket_id });
            }
        } else {
            // Fallback original logic
            await supabase.from('tickets').update({ date_maj: new Date().toISOString(), statut: TicketStatus.IN_PROGRESS }).eq('id', data.ticket_id);
        }
    },

    updateTicketStatus: async (id: number, status: TicketStatus) => {
        await supabase.from('tickets').update({ statut: status }).eq('id', id);
    },
    updateTicketMetadata: async (id: number, meta: any) => { await supabase.from('tickets').update(meta).eq('id', id); },

    syncProductStockStatus: async (id: number) => { await deliveryService.syncProductStatus(id); },
    updateProductStatus: async (id: number, statut: ProductStatus): Promise<void> => {
        const { error } = await supabase.from('products').update({ statut }).eq('id', id);
        if (error) throw new Error(translateError(error));
    },
    uploadClientFile: async (file: File, userId: string): Promise<string> => { return uploadFile('client_uploads', file, `${userId}/${Date.now()}_${file.name}`); },

    getNotificationsForUser: async (userId: string, filters?: NotificationFilters): Promise<Notification[]> => {
        try {
            const limit = filters?.limit ?? 10;
            // Utiliser la fonction RPC si disponible
            const { data, error } = await supabase.rpc('get_user_notifications', {
                p_user_id: userId,
                p_limit: limit,
                p_offset: filters?.offset || 0,
                p_unread_only: filters?.unread_only || false,
                p_type_filter: filters?.type_filter || null
            });

            if (!error && data) {
                return data.map((n: any) => ({
                    ...n,
                    // Compatibilité avec ancien format
                    date: n.created_at,
                    lu_a: n.read_at
                }));
            }

            // Fallback vers l'ancienne méthode
            const query = supabase
                .from('notifications')
                .select('*')
                .eq('user_id', userId);

            if (filters?.unread_only) {
                query.is('read_at', null);
            }

            if (filters?.type_filter) {
                query.eq('type', filters.type_filter);
            }

            const { data: fallbackData } = await query
                .order('created_at', { ascending: false })
                .limit(limit)
                .range(filters?.offset || 0, (filters?.offset || 0) + limit - 1);

            return (fallbackData || []).map((n: any) => ({
                ...n,
                priority: (n.priority as NotificationPriority) || NotificationPriority.NORMAL,
                metadata: n.metadata || {},
                channels: n.channels || ['in_app'],
                read_channels: n.read_channels || [],
                read_at: n.read_at || n.lu_a,
                created_at: n.created_at || n.date,
                // Compatibilité
                date: n.created_at || n.date,
                lu_a: n.read_at || n.lu_a
            }));
        } catch (err) {
            devError('Error getting notifications:', err);
            return [];
        }
    },

    deleteNotification: async (id: string): Promise<void> => {
        const { data, error } = await supabase.rpc('delete_notification', { p_notification_id: id });
        if (error) throw new Error(translateError(error));
        if (data === false) throw new Error('Notification introuvable ou déjà supprimée');
    },

    markNotificationAsRead: async (id: string, channel: string = 'in_app') => {
        try {
            const { error } = await supabase.rpc('mark_notification_read', {
                p_notification_id: id,
                p_channel: channel
            });

            if (error) {
                // Fallback vers l'ancienne méthode
                await supabase.from('notifications').update({ 
                    read_at: new Date().toISOString(),
                    lu_a: new Date().toISOString() // Compatibilité
                }).eq('id', id);
            }
        } catch (err) {
            devError('Error marking notification as read:', err);
        }
    },

    markAllNotificationsAsRead: async (userId: string) => {
        try {
            // Récupérer toutes les notifications non lues
            const { data: unreadNotifications } = await supabase
                .from('notifications')
                .select('id, read_channels')
                .eq('user_id', userId)
                .is('read_at', null);

            if (unreadNotifications && unreadNotifications.length > 0) {
                // Marquer chaque notification comme lue
                for (const notif of unreadNotifications) {
                    const readChannels = Array.isArray(notif.read_channels) ? [...notif.read_channels] : [];
                    if (!readChannels.includes('in_app')) {
                        readChannels.push('in_app');
                    }

                    await supabase
                        .from('notifications')
                        .update({ 
                            read_at: new Date().toISOString(),
                            read_channels: readChannels,
                            lu_a: new Date().toISOString() // Compatibilité
                        })
                        .eq('id', notif.id);
                }
            }
        } catch (err) {
            devError('Error marking all notifications as read:', err);
        }
    },

    /** Marque comme lues toutes les notifications non lues d'une section (à appeler à l'ouverture de la page). */
    markNotificationsAsReadBySection: async (userId: string, section: 'orders' | 'deposits' | 'tickets' | 'clients'): Promise<void> => {
        const typesBySection: Record<string, string[]> = {
            orders: ['commande_statut', 'commande_creee', 'commande_payee', 'commande_livree', 'commande_annulee', 'commande_litige'],
            deposits: ['depot_valide', 'depot_refuse', 'depot_cree'],
            tickets: ['ticket_reponse', 'ticket_statut', 'ticket_cree', 'ticket_urgence', 'nouveau_ticket'],
            clients: ['nouveau_client']
        };
        const types = typesBySection[section];
        if (!types?.length) return;
        try {
            const { data: unread } = await supabase
                .from('notifications')
                .select('id, read_channels')
                .eq('user_id', userId)
                .is('read_at', null)
                .in('type', types);
            if (!unread?.length) return;
            const now = new Date().toISOString();
            for (const n of unread) {
                const readChannels = Array.isArray(n.read_channels) ? [...n.read_channels] : [];
                if (!readChannels.includes('in_app')) readChannels.push('in_app');
                await supabase.from('notifications').update({
                    read_at: now,
                    read_channels: readChannels,
                    lu_a: now
                }).eq('id', n.id);
            }
        } catch (err) {
            devError('Error marking notifications as read by section:', err);
        }
    },

    getUnreadCount: async (userId: string, typeFilter?: NotificationType): Promise<number> => {
        try {
            const { data, error } = await supabase.rpc('get_unread_count', {
                p_user_id: userId,
                p_type_filter: typeFilter || null
            });

            if (!error && data !== null) {
                return data;
            }

            // Fallback
            const query = supabase
                .from('notifications')
                .select('id', { count: 'exact', head: true })
                .eq('user_id', userId)
                .is('read_at', null);

            if (typeFilter) {
                query.eq('type', typeFilter);
            }

            const { count } = await query;
            return count || 0;
        } catch (err) {
            devError('Error getting unread count:', err);
            return 0;
        }
    },

    /** Un seul appel : retourne les compteurs de notifications non lues par section (orders, deposits, tickets, clients). */
    getUnreadCountsBySection: async (userId: string): Promise<{ orders: number; deposits: number; tickets: number; clients: number }> => {
        const empty = { orders: 0, deposits: 0, tickets: 0, clients: 0 };
        try {
            const list = await api.getNotificationsForUser(userId, { unread_only: true, limit: 500 });
            const orders = ['commande_statut', 'commande_creee', 'commande_payee', 'commande_livree', 'commande_annulee', 'commande_litige'];
            const deposits = ['depot_valide', 'depot_refuse', 'depot_cree'];
            const tickets = ['ticket_reponse', 'ticket_statut', 'ticket_cree', 'ticket_urgence', 'nouveau_ticket'];
            const counts = { orders: 0, deposits: 0, tickets: 0, clients: 0 };
            for (const n of list) {
                const t = (n.type || '').toLowerCase();
                if (orders.includes(t)) counts.orders++;
                else if (deposits.includes(t)) counts.deposits++;
                else if (tickets.includes(t)) counts.tickets++;
                else if (t === 'nouveau_client') counts.clients++;
            }
            return counts;
        } catch (err) {
            devError('Error getting unread counts by section:', err);
            return empty;
        }
    },

    createNotification: async (input: CreateNotificationInput): Promise<string | null> => {
        try {
            const { data, error } = await supabase.rpc('create_notification', {
                p_user_id: input.user_id,
                p_type: input.type,
                p_message: input.message,
                p_lien: input.lien,
                p_priority: input.priority || NotificationPriority.NORMAL,
                p_metadata: input.metadata || {},
                p_expires_at: input.expires_at || null,
                p_channels: input.channels || ['in_app']
            });

            if (error) {
                devError('Error creating notification:', error);
                return null;
            }

            return data;
        } catch (err) {
            devError('Error creating notification:', err);
            return null;
        }
    },
    subscribeToNewsletter: async (email: string) => { await supabase.from('newsletter_subscribers').insert({ email }); },
    getFavorites: async (userId: string): Promise<number[]> => {
        const { data } = await supabase.from('favorites').select('product_id').eq('user_id', userId);
        return data?.map(f => f.product_id) || [];
    },
    addFavorite: async (userId: string, productId: number) => { await supabase.from('favorites').insert({ user_id: userId, product_id: productId }); },
    removeFavorite: async (userId: string, productId: number) => { await supabase.from('favorites').delete().match({ user_id: userId, product_id: productId }); },

    getAppLogoUrl: (theme: 'light' | 'dark' = 'light'): string => {
        return theme === 'dark'
            ? 'https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/logo_dark.png'
            : 'https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/logo_light.png';
    },

    updateAppLogo: async (file: File, theme: 'light' | 'dark'): Promise<string> => {
        const path = `branding/logo_${theme}.png`;
        return await uploadFile('public_pic', file, path);
    },

    // 2FA Methods
    get2FAStatus: async (userId: string): Promise<{ two_fa_enabled: boolean; last_2fa_at: string | null }> => {
        const { data, error } = await supabase
            .from('user_security')
            .select('two_fa_enabled, last_2fa_at')
            .eq('user_id', userId)
            .single();
        
        if (error && error.code !== 'PGRST116') throw new Error(translateError(error));
        return data || { two_fa_enabled: false, last_2fa_at: null };
    },

    generate2FASecret: async (userId: string, email: string): Promise<{ secret: string; qrCodeUrl: string; manualEntryKey: string }> => {
        // Générer un secret TOTP valide en Base32 RFC4648
        // Base32 RFC4648 strict : A-Z (sauf I et O) et 2-7 uniquement
        // Caractères interdits : I, O, 0, 1, 8
        const generateBase32Secret = () => {
            // Alphabet Base32 RFC4648 strict (sans I, O, 0, 1, 8)
            // Utilise uniquement : A-H, J-N, P-Z, 2-7
            const base32Chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ234567';
            let secret = '';
            // Générer 32 caractères (256 bits) pour un secret fort
            const crypto = window.crypto || (window as any).msCrypto;
            if (crypto && crypto.getRandomValues) {
                // Utiliser crypto.getRandomValues pour une meilleure sécurité
                const array = new Uint8Array(20); // 20 bytes = 32 caractères base32
                crypto.getRandomValues(array);
                for (let i = 0; i < 20; i++) {
                    secret += base32Chars[array[i] % base32Chars.length];
                }
                // Compléter à 32 caractères si nécessaire
                while (secret.length < 32) {
                    secret += base32Chars[Math.floor(Math.random() * base32Chars.length)];
                }
            } else {
                // Fallback si crypto n'est pas disponible
                for (let i = 0; i < 32; i++) {
                    secret += base32Chars.charAt(Math.floor(Math.random() * base32Chars.length));
                }
            }
            return secret;
        };

        const secret = generateBase32Secret();
        const issuer = 'eServices';
        const accountName = email;
        
        // Format manuel : secret brut (sans tirets) pour Google Authenticator
        // Google Authenticator n'accepte PAS les tirets, espaces ou séparateurs
        const manualEntryKey = secret; // Secret brut sans séparateurs
        
        // URL pour QR Code (format otpauth://)
        const qrCodeUrl = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;

        // Stocker temporairement le secret (non chiffré pour l'instant, sera chiffré lors de l'activation)
        const { error } = await supabase
            .from('user_security')
            .upsert({
                user_id: userId,
                totp_secret: secret, // En production, chiffrer ce secret côté serveur via RPC
                two_fa_enabled: false
            }, { onConflict: 'user_id' });

        if (error) throw new Error(translateError(error));

        return { secret, qrCodeUrl, manualEntryKey };
    },

    verify2FACode: async (userId: string, code: string): Promise<{ valid: boolean; recoveryCodes?: string[] }> => {
        // Récupérer le secret
        const { data, error } = await supabase
            .from('user_security')
            .select('totp_secret')
            .eq('user_id', userId)
            .single();

        if (error || !data?.totp_secret) {
            throw new Error('Secret TOTP non trouvé');
        }

        // Vérifier le code TOTP côté client (en production, utiliser une fonction RPC Supabase)
        const isValid = await verifyTOTPClient(data.totp_secret, code);
        
        if (!isValid) {
            return { valid: false };
        }

        // Générer les codes de récupération
        const recoveryCodes = generateRecoveryCodes();
        const hashedCodes = recoveryCodes.map(code => hashRecoveryCode(code));

        // Activer la 2FA
        const { error: updateError } = await supabase
            .from('user_security')
            .update({
                two_fa_enabled: true,
                recovery_codes: hashedCodes,
                last_2fa_at: new Date().toISOString()
            })
            .eq('user_id', userId);

        if (updateError) throw new Error(translateError(updateError));

        return { valid: true, recoveryCodes };
    },

    disable2FA: async (userId: string, password: string): Promise<void> => {
        // Vérifier le mot de passe avant de désactiver
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Session introuvable');

        const { error: signInError } = await supabase.auth.signInWithPassword({
            email: session.user.email!,
            password: password
        });

        if (signInError) throw new Error('Mot de passe incorrect');

        // Utiliser upsert pour créer la ligne si elle n'existe pas, ou la mettre à jour si elle existe
        const { error } = await supabase
            .from('user_security')
            .upsert({
                user_id: userId,
                two_fa_enabled: false,
                totp_secret: null,
                recovery_codes: null,
                last_2fa_at: null
            }, {
                onConflict: 'user_id'
            });

        if (error) throw new Error(translateError(error));
    },

    regenerateRecoveryCodes: async (userId: string): Promise<string[]> => {
        const recoveryCodes = generateRecoveryCodes();
        const hashedCodes = recoveryCodes.map(code => hashRecoveryCode(code));

        const { error } = await supabase
            .from('user_security')
            .update({ recovery_codes: hashedCodes })
            .eq('user_id', userId);

        if (error) throw new Error(translateError(error));

        return recoveryCodes;
    },

    getDeliverySettings: async (): Promise<Record<string, string>> => {
        const { data, error } = await supabase.from('admin_delivery_settings').select('key, value');
        if (error) throw new Error(translateError(error));
        const settings: Record<string, string> = {};
        (data || []).forEach((row: any) => { settings[row.key] = row.value; });
        return settings;
    },

    updateDeliverySetting: async (key: string, value: string): Promise<void> => {
        const { error } = await supabase.from('admin_delivery_settings').upsert(
            { key, value, updated_at: new Date().toISOString() },
            { onConflict: 'key' }
        );
        if (error) throw new Error(translateError(error));
    },

    generateTrackingNumber: (orderId: number): string => {
        const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
        return `ES-TRK-${orderId}-${rand}`;
    },
};